import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { Trabajador } from '@shared/models';
import { ResponseWrapper } from '@shared';
import { environment } from '@env';

const API_URL = environment.URL_SERVICIOSBACK;
const ENDPOINT_TRABAJADORES = '/trabajadores';

@Injectable({
  providedIn: 'root',
})
export class TrabajadoresService {
  private readonly API_URL_TRABAJADORES = API_URL + ENDPOINT_TRABAJADORES;
  private http = inject(HttpClient);

  listarTrabajadores(params?: { empresaId?: number; categoriaRenta?: string }): Observable<Trabajador[]> {
    let httpParams = new HttpParams();
    if (params?.empresaId !== undefined) {
      httpParams = httpParams.set('empresaId', params.empresaId);
    }
    if (params?.categoriaRenta !== undefined) {
      httpParams = httpParams.set('categoriaRenta', params.categoriaRenta);
    }
    return this.http
      .get<ResponseWrapper<Trabajador[]>>(this.API_URL_TRABAJADORES, { params: httpParams })
      .pipe(map((res) => res.data));
  }
  registrarIntegral(payload: any): Observable<HttpResponse<ResponseWrapper<any>>> {
    return this.http.post<ResponseWrapper<any>>(
      `${this.API_URL_TRABAJADORES}/registro-integral`, 
      payload, 
      { observe: 'response' }
    );
  }

  verificarEmailDisponible(email: string, trabajadorId?: number | null): Observable<boolean> {
    let params = new HttpParams().set('email', email.trim().toLowerCase());
    if (trabajadorId != null) {
      params = params.set('trabajadorId', trabajadorId.toString());
    }
    return this.http
      .get<ResponseWrapper<{ disponible: boolean }>>(`${this.API_URL_TRABAJADORES}/email-disponible`, { params })
      .pipe(map((res) => res.data.disponible));
  }

  buscarPorDocumento(tipoId: number, numero: string): Observable<Trabajador> {
    return this.http
      .get<ResponseWrapper<Trabajador>>(`${this.API_URL_TRABAJADORES}/buscar-documento/${tipoId}/${numero}`)
      .pipe(map(res => res.data));
  }
  desactivarTrabajador(
    trabajadorId: number,
    body: { fechaBaja?: string; motivoBajaId?: number }
  ): Observable<HttpResponse<ResponseWrapper<any>>> {
    return this.http.patch<ResponseWrapper<any>>(
      `${this.API_URL_TRABAJADORES}/desactivar/${trabajadorId}`,
      body,
      { observe: 'response' }
    );
  }

  registrarTrabajador(trabajador: Trabajador): Observable<HttpResponse<ResponseWrapper<Trabajador>>> {
    return this.http.post<ResponseWrapper<Trabajador>>(this.API_URL_TRABAJADORES, trabajador, { observe: 'response' });
  }
  modificarTrabajador(trabajadorId: number, trabajador: Trabajador): Observable<HttpResponse<ResponseWrapper<Trabajador>>> {
    return this.http.put<ResponseWrapper<Trabajador>>(`${this.API_URL_TRABAJADORES}/${trabajadorId}`, trabajador, { observe: 'response' });
  }
  modificarContrato(contratoId: number, contrato: any): Observable<HttpResponse<ResponseWrapper<any>>> {
    return this.http.put<ResponseWrapper<any>>(
      `${environment.URL_SERVICIOSBACK}/rrhh/contrato/${contratoId}`, 
      contrato, 
      { observe: 'response' }
    );
  }
  obtenerTrabajadorPorId(id: number): Observable<Trabajador> {
    return this.http.get<ResponseWrapper<Trabajador>>(`${this.API_URL_TRABAJADORES}/${id}`)
      .pipe(map(res => res.data));
  }
  obtenerContratoActivo(trabajadorId: number): Observable<any> {
    return this.http.get<ResponseWrapper<any>>(`${environment.URL_SERVICIOSBACK}/rrhh/contrato/trabajador/${trabajadorId}/activo`)
      .pipe(map(res => res.data));
  }
  activarTrabajador(trabajadorId: number): Observable<HttpResponse<ResponseWrapper<Trabajador>>> {
    return this.http.patch<ResponseWrapper<Trabajador>>(
      `${this.API_URL_TRABAJADORES}/activar/${trabajadorId}`,
      {},
      { observe: 'response' }
    );
  }
  listarTrabajadoresIntegral(params: { empresaId: number }): Observable<Trabajador[]> {
    return this.http
      .get<ResponseWrapper<Trabajador[]>>(`${this.API_URL_TRABAJADORES}/empresa/${params.empresaId}/integral`)
      .pipe(map((res) => res.data));
}
subirCv(trabajadorId: number, empresaId: number, file: File): Observable<HttpResponse<ResponseWrapper<any>>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('empresaId', empresaId.toString());

    return this.http.post<ResponseWrapper<any>>(
      `${this.API_URL_TRABAJADORES}/${trabajadorId}/subir-cv`,
      formData,
      { observe: 'response' }
    );
  }

  subirFoto(trabajadorId: number, empresaId: number, file: File): Observable<HttpResponse<ResponseWrapper<any>>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('empresaId', empresaId.toString());

    return this.http.post<ResponseWrapper<any>>(
      `${this.API_URL_TRABAJADORES}/${trabajadorId}/subir-foto`,
      formData,
      { observe: 'response' }
    );
  }

  descargarCv(trabajadorId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL_TRABAJADORES}/${trabajadorId}/descargar-cv`, {
      responseType: 'blob' 
    });
  }

  descargarFoto(trabajadorId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL_TRABAJADORES}/${trabajadorId}/descargar-foto`, {
      responseType: 'blob' 
    });
  }
  

  descargarReporteGeneral(empresaId: number, categoriaRenta: string): Observable<Blob> {
    const httpParams = new HttpParams()
      .set('empresaId', empresaId)
      .set('categoriaRenta', categoriaRenta);
    const endpoint = categoriaRenta === '4TA' ? '/reporte-general/4ta' : '/reporte-general';

    return this.http.get(`${this.API_URL_TRABAJADORES}${endpoint}`, {
      params: httpParams,
      responseType: 'blob'
    });
  }

  descargarPlantillaMasiva(empresaId: number, categoriaRenta: string): Observable<Blob> {
    let httpParams = new HttpParams().set('empresaId', empresaId);
    const endpoint = categoriaRenta === '4TA' ? '/descargar-plantilla/4ta' : '/descargar-plantilla';

    return this.http.get(`${this.API_URL_TRABAJADORES}${endpoint}`, {
      params: httpParams,
      responseType: 'blob'
    });
  }

  cargaMasivaTrabajadores(empresaId: number, file: File, categoriaRenta: string): Observable<HttpResponse<ResponseWrapper<any>>> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('empresaId', empresaId.toString());

    const endpoint = categoriaRenta === '4TA' ? '/carga-masiva/4ta' : '/carga-masiva';

    return this.http.post<ResponseWrapper<any>>(
      `${this.API_URL_TRABAJADORES}${endpoint}`,
      formData,
      { 
        params: params, 
        observe: 'response' 
      }
    );
  }

  listarTrabajadoresActivosConPuestos(empresaId: number): Observable<any> {
    // Usamos directamente el environment en lugar de this.baseUrl
    return this.http.get<any>(`${environment.URL_SERVICIOSBACK}/trabajadores/activos-con-puestos?empresaId=${empresaId}`);
  }
  obtenerEscalaVigente(empresaId: number, puestoId: number, fecha?: string): Observable<any> {
    let params = new HttpParams()
      .set('empresaId', empresaId.toString())
      .set('puestoId', puestoId.toString());
      
    if (fecha) {
      params = params.set('fecha', fecha);
    }
    return this.http.get<any>(`${environment.URL_SERVICIOSBACK}/escalas-salariales/vigente`, { params });
  }

  listarTrabajadoresParaSolicitudes(empresaId: number): Observable<any> {
  // Asegúrate de que esta ruta sea idéntica a la del @GetMapping en tu Java
  return this.http.get<any>(`${environment.URL_SERVICIOSBACK}/trabajadores/para-solicitudes?empresaId=${empresaId}`);
}
}
